import {useEffect, useLayoutEffect, useMemo} from 'react';
import {Node, OnNodesChange, useNodesInitialized, useNodesState} from 'reactflow';

import {assert} from 'Asserts';
import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {BaseCondition} from 'studies/typed/shared/useExperimentDesignState';
import {unhideFlowchartElementAfterLayout} from 'ui-library/reactflow/layout/hideFlowchartElementBeforeLayoutUtils';

import {
  FALLBACK_COLUMN_NODE_HEIGHT,
  ProcedureRunDesignFlowchartColumnNodeData,
} from './ProcedureRunDesignFlowchartColumnNode';
import {
  getMaxMeasuredNodeHeight,
  GridLayoutOptions,
  makeInitialNodes,
  recomputeColumnPositions,
  recomputeGridPositions,
} from './ProcedureRunDesignFlowchartLayoutUtils';
import {ProcedureRunDesignFlowchartNodeData} from './ProcedureRunDesignFlowchartNode';

export interface ProcedureRunDesignFlowchartLayoutOptions extends GridLayoutOptions {
  columnHeaderBottomGap: number;
}

type FlowchartNodeData = ProcedureRunDesignFlowchartNodeData | ProcedureRunDesignFlowchartColumnNodeData;
type FlowchartNode = Node<FlowchartNodeData>;

/**
 * Manages the two-phase layout for ProcedureRunDesignFlowchart:
 * 1. Create hidden placeholder nodes so ReactFlow can measure their dimensions.
 * 2. Once dimensions are available, compute grid positions and unhide nodes.
 */
export function useProcedureRunDesignFlowchartLayout(
  conditions: Array<BaseCondition>,
  methods: Array<ExperimentMethod>,
  layoutOptions: ProcedureRunDesignFlowchartLayoutOptions
): {
  nodes: Array<FlowchartNode>;
  onNodesChange: OnNodesChange;
} {
  // A procedure run design always has at least one condition
  assert(conditions.length >= 1, 'Expected at least one condition');

  const {columnNodes: initialColumnNodes, rows: initialRows} = useMemo(
    () => makeInitialNodes(conditions, methods),
    [conditions, methods]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState<FlowchartNodeData>([
    ...initialColumnNodes,
    ...initialRows.flat(),
  ]);

  // Sync nodes when conditions/methods change.
  // For each new node, preserve position/dims/visibility from the existing node if it already
  // exists in state; otherwise start it hidden at the origin for ReactFlow to measure.
  useEffect(() => {
    setNodes((currentNodes) => {
      const allNewNodes = [...initialColumnNodes, ...initialRows.flat()];
      const existingById = new Map(currentNodes.map((n) => [n.id, n]));

      return allNewNodes.map((newNode) => {
        const existing = existingById.get(newNode.id);
        // Existing node: preserve position, dims, and visibility; only refresh data.
        // New node: starts hidden at origin, ready for ReactFlow measurement.
        return existing ? {...existing, data: newNode.data} : newNode;
      });
    });
  }, [initialColumnNodes, initialRows, setNodes]);

  const nodesInitialized = useNodesInitialized();

  // Hash id + dimensions only — position-only changes produce the same hash,
  // preventing setNodes -> hash-change -> re-layout loops
  const nodeLayoutDepsHash = JSON.stringify(nodes.map(({id, height, width}) => ({id, height, width})));

  useLayoutEffect(() => {
    if (!nodesInitialized) {
      return;
    }

    const expectedIds = new Set([
      ...initialColumnNodes.map((n) => n.id),
      ...initialRows.flat().map((n) => n.id),
    ]);

    setNodes((currentNodes: Array<FlowchartNode>) => {
      const measuredNodesById = new Map(currentNodes.map((currentNode) => [currentNode.id, currentNode]));

      // Lay out only once every expected node has been measured. Until then, leave the current
      // nodes untouched (see the early return below) and wait for the next measurement.
      const hasLayout = [...expectedIds].every((id) => {
        const measuredNode = measuredNodesById.get(id);
        return measuredNode != null && (measuredNode.width ?? 0) > 0 && (measuredNode.height ?? 0) > 0;
      });

      if (!hasLayout) {
        // Some expected nodes are not yet measured (e.g. a newly-added replicate node).
        // Don't disturb existing visible nodes — leave state as-is and wait for the next
        // nodeLayoutDepsHash change.
        return currentNodes;
      }
      // The hasLayout guard above guarantees this entry exists and is measured.
      const recomputedNodes = recomputeNodePositions(
        initialRows,
        initialColumnNodes,
        measuredNodesById,
        layoutOptions
      );
      return recomputedNodes.map((node) => unhideFlowchartElementAfterLayout(node));
    });
    // nodeLayoutDepsHash captures dimension changes without including positions, so layout only
    // re-runs when sizes change, not when setNodes updates positions. layoutOptions is expected
    // to be a stable module-level constant at the call site.
  }, [nodesInitialized, nodeLayoutDepsHash, initialRows, initialColumnNodes, setNodes, layoutOptions]);

  return {nodes, onNodesChange};
}

/**
 * Recomputes the positions of the nodes in the flowchart.
 * This function is called when all expected nodes are measured.
 * It recomputes the positions of the nodes in the flowchart and returns the recomputed nodes.
 */
function recomputeNodePositions(
  initialRows: Array<Array<FlowchartNode>>,
  initialColumnNodes: Array<FlowchartNode>,
  measuredNodesById: Map<string, FlowchartNode>,
  layoutOptions: ProcedureRunDesignFlowchartLayoutOptions
): Array<FlowchartNode> {
  const measuredColumnNodes = initialColumnNodes.map((columnNode) => {
    const {width, height} = measuredNodesById.get(columnNode.id) ?? columnNode;
    return {...columnNode, width, height};
  });

  const measuredRows = initialRows.map((row) =>
    row.map((rowNode) => {
      // The hasLayout guard above guarantees this entry exists and is measured.
      const {width, height} = measuredNodesById.get(rowNode.id) ?? rowNode;
      return {...rowNode, width, height};
    })
  );

  // Grid rows start below the column headers
  const columnHeaderHeight = getMaxMeasuredNodeHeight(measuredColumnNodes, FALLBACK_COLUMN_NODE_HEIGHT);

  const {repositionedRows, columnDimensions} = recomputeGridPositions(measuredRows, {
    horizontalGap: layoutOptions.horizontalGap,
    verticalGap: layoutOptions.verticalGap,
    startX: layoutOptions.startX,
    startY: layoutOptions.startY + columnHeaderHeight + layoutOptions.columnHeaderBottomGap,
  });

  const repositionedColumnNodes = recomputeColumnPositions(measuredColumnNodes, columnDimensions, {
    startY: layoutOptions.startY,
  });

  return [...repositionedColumnNodes, ...repositionedRows.flat()];
}
